import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';

import {
  COUNTRIES_WITH_STATES,
  DEMO_FORM_ID,
  MarketingFields,
  MARKETO_BASE_URL,
  MARKETO_MUNCHKIN_ID,
} from 'components/standalonePages/common/constants';

import { gql, gqlRequest } from 'common/util/gqlRequest';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { useMarketo } from 'hooks/useMarketo';
import './FormPages.scss';
import ELEMENT from 'common/element';
import MarketingForm from './common/MarketingForm';

const marketingFormValidationSchema = yup.object({
  firstName: yup.string().required('Required').max(100),
  lastName: yup.string().required('Required').max(100),
  email: yup.string().email('Please enter a valid email address').required('Required'),
  phoneNumber: yup.string().required('Required').max(100),
  company: yup.string().required('Required').max(100),
  country: yup.string().required('Required'),
  state: yup.string().when('country', {
    is: (country: COUNTRIES_WITH_STATES) => Object.values(COUNTRIES_WITH_STATES).includes(country),
    then: yup.string().required('Required'),
  }),
  numberOfEmployees: yup.string().required('Required'),
});

const marketingFormInitialValues: MarketingFields = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  company: '',
  country: '',
  state: '',
  numberOfEmployees: '',
};

/**
 * Hook that contains logic to check for the demo account availability
 */
function useDemoAccountAvailabilityCheck(): boolean {
  const [isDemoAvailable, setIsDemoAvailable] = useState(true);

  useEffect(() => {
    async function verifyIsDemoAvailable() {
      const IS_DEMO_ACCOUNT_AVAILABLE = gql`
        query isDemoAccountAvailable {
          isDemoAccountAvailable
        }
      `;

      try {
        const isAvailable: boolean = await gqlRequest({ query: IS_DEMO_ACCOUNT_AVAILABLE });
        setIsDemoAvailable(isAvailable);
      } catch (e) {
        setIsDemoAvailable(false);
      }
    }

    verifyIsDemoAvailable();
  }, []);

  return isDemoAvailable;
}

/**
 * Standalone Try Scalyr page that allows user to login into Scalyr web-app under a Demo account.
 * Collects additional fields such as name, company, utm_*, etc… for marketing purposes. Values submitted to HubSpot via Form API
 *
 * @example
 * // insert TryScalyr form on Scalyr marketing page
 *
 * // for US website:
 * <iframe height={400} width={460} frameBorder={0} title="US Try Scalyr Form" src="https://app.scalyr.com/tryScalyrForm" />
 * // for EU website:
 * <iframe height={400} width={460} frameBorder={0} title="EU Try Scalyr Form" src="https://app.eu.scalyr.com/tryScalyrForm" />
 */
function TryScalyrForm(): JSX.Element {
  const [error, setError] = useState<Error>();
  const isDemoAccountAvailable = useDemoAccountAvailabilityCheck();
  const marketoScriptLoaded = useMarketo(MARKETO_BASE_URL, MARKETO_MUNCHKIN_ID, DEMO_FORM_ID);

  async function loginToDemoAccount() {
    const LOGIN_TO_DEMO_ACCOUNT = gql`
      mutation loginToDemoAccount($timezoneOffset: String!) {
        loginToDemoAccount(timezoneOffset: $timezoneOffset)
      }
    `;

    await gqlRequest({
      mutation: LOGIN_TO_DEMO_ACCOUNT,
      variables: {
        timezoneOffset: new Date().getTimezoneOffset(),
      },
    });
  }
  async function handleFormSubmit(values: MarketingFields) {
    const { email, firstName, lastName, phoneNumber, company, country, numberOfEmployees, state } = values;
    const urlParams = new URLSearchParams(window.self.location.search);

    try {
      if (marketoScriptLoaded) {
        const mktoForm = window.MktoForms2.allForms()[0];
        mktoForm.setValues({
          Email: email,
          FirstName: firstName,
          LastName: lastName,
          Phone: phoneNumber,
          Company: company,
          Country: country,
          Employees__c: numberOfEmployees,
          State: state,
          UTM_Campaign__c: urlParams.get('UTM_Campaign'),
          UTM_Content__c: urlParams.get('UTM_Content'),
          UTM_Medium__c: urlParams.get('UTM_Medium'),
          UTM_Source__c: urlParams.get('UTM_Source'),
          UTM_Term__c: urlParams.get('UTM_Term'),
          GCLID__c: urlParams.get('gclid'),
          WEBSITE_OPT_IN__c: urlParams.get('website_opt_in'),
        });
        mktoForm.submit();
        mktoForm.onSuccess(function () {
          loginToDemoAccount();
        });
      }
    } catch (err) {
      setError(err);
    }
  }

  if (!isDemoAccountAvailable) {
    return (
      <div className="FormPages__MessageContainer">
        <p>
          The live demo appears to be at capacity at the moment. Please check back in a few minutes. Things should free
          up shortly. In the meantime, if you have any questions, feel free to{' '}
          <a target="_blank" href="https://www.scalyr.com/contact">
            contact us
          </a>
        </p>
      </div>
    );
  }

  return (
    <Formik
      validationSchema={marketingFormValidationSchema}
      validateOnChange={false}
      onSubmit={handleFormSubmit}
      initialValues={marketingFormInitialValues}
    >
      {({ handleChange, values, touched, errors, isSubmitting, submitForm }): JSX.Element => (
        <div className="FormPages__Container">
          {error && <div className="error-message-block form-group">{error?.message}</div>}
          <MarketingForm values={values} touched={touched} errors={errors} handleChange={handleChange} />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setError(undefined);
              submitForm();
            }}
            disabled={isSubmitting}
            className="FormPages__Button"
            testId={ELEMENT.marketingFormContinueButton}
          >
            Continue
          </Button>
          <form hidden id="mktoForm_6501"></form>
        </div>
      )}
    </Formik>
  );
}

export default TryScalyrForm;
